// src/components/notifications/NotificationsInbox.tsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Divider,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  DoneAll,
  DeleteSweep,
  Circle,
  Campaign,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { notificationsAPI, type Notification } from '../../services/api';
import { useNotifications } from '../../hooks/useNotifications';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalToast } from '../../App';
import { Permissions } from '../../utils/permissions';
import BroadcastMessageDialog, {
  type BroadcastMessageData,
} from './BroadcastMessageDialog';

const CATEGORIES = ['All', 'System', 'Booking', 'Quote', 'Backup'] as const;
type Category = (typeof CATEGORIES)[number];

const CATEGORY_COLORS: Record<
  string,
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  System: 'info',
  Backup: 'success',
  Quote: 'warning',
  Booking: 'default',
};

const InboxWrap = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const ToolbarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 4px',
  minHeight: '48px',
});

const FilterRow = styled(Box)({
  display: 'flex',
  gap: '6px',
  padding: '4px 4px 8px',
  flexWrap: 'wrap',
});

const NotificationRow = styled(Box, {
  shouldForwardProp: (p) => p !== 'unread',
})<{ unread?: boolean }>(({ theme, unread }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  padding: '12px 4px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: unread
    ? theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.04)'
      : 'rgba(0,0,0,0.02)'
    : 'transparent',
  transition: 'background-color 0.15s',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: '48px 0',
  textAlign: 'center',
  color: theme.palette.text.disabled,
}));

interface Props {
  onUnreadCountChange?: (count: number) => void;
}

const NotificationsInbox: React.FC<Props> = ({ onUnreadCountChange }) => {
  const {
    notifications,
    unreadCount,
    loading,
    fetchAll,
    markRead,
    markAllRead,
    remove,
    removeBulk,
  } = useNotifications();

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [activeFilter, setActiveFilter] = useState<Category>('All');

  const { user } = useAuth();
  const { showSuccess, showError } = useGlobalToast();
  const canBroadcast = Permissions.canBroadcastMessages(user?.role);
  const [composerOpen, setComposerOpen] = useState(false);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    onUnreadCountChange?.(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const filtered =
    activeFilter === 'All'
      ? notifications
      : notifications.filter((n) => n.category === activeFilter);

  const deletable = filtered.filter((n) => !n.isBroadcast);
  const allSelected =
    deletable.length > 0 && selected.size === deletable.length;
  const someSelected = selected.size > 0;

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(
      allSelected
        ? new Set()
        : new Set(deletable.map((n: Notification) => n.id))
    );
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
  };

  const handleDeleteSelected = async () => {
    await removeBulk(Array.from(selected));
    setSelected(new Set());
  };

  const handleRowClick = async (n: Notification) => {
    if (!n.isRead) await markRead(n.id);
  };

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    await remove(id);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleBroadcastConfirm = async (data: BroadcastMessageData) => {
    setSendingBroadcast(true);
    try {
      await notificationsAPI.broadcast(data.title, data.body, data.expiresAt);
      setComposerOpen(false);
      showSuccess('Broadcast sent.');
      fetchAll();
    } catch {
      showError('Failed to send broadcast.');
    } finally {
      setSendingBroadcast(false);
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={64} />
        ))}
      </Box>
    );
  }

  return (
    <InboxWrap>
      <ToolbarRow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {deletable.length > 0 && (
            <Checkbox
              size="small"
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={toggleSelectAll}
              aria-label="Select all"
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {filtered.length === 0
              ? 'No notifications'
              : `${filtered.length} notification${filtered.length !== 1 ? 's' : ''}${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {canBroadcast && (
            <Tooltip title="Broadcast a message">
              <IconButton
                size="small"
                onClick={() => setComposerOpen(true)}
                aria-label="Broadcast a message"
              >
                <Campaign />
              </IconButton>
            </Tooltip>
          )}
          {someSelected ? (
            <Tooltip title="Delete selected">
              <span>
                <IconButton
                  size="small"
                  onClick={handleDeleteSelected}
                  color="error"
                  aria-label="Delete selected"
                >
                  <DeleteSweep />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            unreadCount > 0 && (
              <Tooltip title="Mark all as read">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleMarkAllRead}
                    aria-label="Mark all as read"
                  >
                    <DoneAll />
                  </IconButton>
                </span>
              </Tooltip>
            )
          )}
        </Box>
      </ToolbarRow>

      <FilterRow>
        {CATEGORIES.map((cat) => {
          const count =
            cat === 'All'
              ? notifications.length
              : notifications.filter((n) => n.category === cat).length;
          return (
            <Chip
              key={cat}
              label={count > 0 ? `${cat} (${count})` : cat}
              size="small"
              color={
                activeFilter === cat
                  ? (CATEGORY_COLORS[cat] ?? 'primary')
                  : 'default'
              }
              variant={activeFilter === cat ? 'filled' : 'outlined'}
              onClick={() => {
                setActiveFilter(cat);
                setSelected(new Set());
              }}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </FilterRow>

      <Divider />

      {filtered.length === 0 ? (
        <EmptyState>
          <Typography variant="body1">You're all caught up.</Typography>
        </EmptyState>
      ) : (
        filtered.map((n: Notification) => (
          <NotificationRow
            key={n.id}
            unread={!n.isRead}
            onClick={() => handleRowClick(n)}
          >
            {!n.isBroadcast && (
              <Checkbox
                size="small"
                checked={selected.has(n.id)}
                onChange={() => toggleSelect(n.id)}
                onClick={(e) => e.stopPropagation()}
                sx={{ mt: '-2px', flexShrink: 0 }}
              />
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                  mb: 0.5,
                }}
              >
                {!n.isRead && (
                  <Circle
                    sx={{
                      fontSize: '0.5rem',
                      color: 'primary.main',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  fontWeight={n.isRead ? 400 : 700}
                  sx={{ lineHeight: 1.3 }}
                >
                  {n.title}
                </Typography>
                <Chip
                  label={n.category}
                  size="small"
                  color={CATEGORY_COLORS[n.category] ?? 'default'}
                  sx={{ height: '18px', fontSize: '0.7rem' }}
                />
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                {n.body}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatDate(n.createdAt)}
              </Typography>
            </Box>

            {!n.isBroadcast && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={(e) => handleDelete(e, n.id)}
                  sx={{ flexShrink: 0, mt: '-4px' }}
                  aria-label={`Delete notification: ${n.title}`}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </NotificationRow>
        ))
      )}

      {canBroadcast && (
        <BroadcastMessageDialog
          open={composerOpen}
          onClose={() => setComposerOpen(false)}
          onConfirm={handleBroadcastConfirm}
          sending={sendingBroadcast}
        />
      )}
    </InboxWrap>
  );
};

export default NotificationsInbox;
